/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editDefendantAppeal2 } from "services/appealService";
import { BASE_URL, LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Prosecutor = {
  user: {
    id: string;
    first_name: string;
    last_name: string;
    employee_organizational_unit: string;
  };
};

type Props = {
  appealId?: number;
  assignedProsecutor?: string;
  onAssigned: () => void;
};

// Inline panel (replaces the defendant assign-prosecutor modal): shows the
// current assignee and lets a coordinator assign or reassign a prosecutor.
const DefendantAssignProsecutorPanel: FC<Props> = ({
  appealId,
  assignedProsecutor,
  onAssigned,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const [prosecutors, setProsecutors] = useState<Prosecutor[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const isAssigned = !!assignedProsecutor;
  const showPicker = !isAssigned || reassigning;

  useEffect(() => {
    if (!loginData || !showPicker) return;
    setLoadingList(true);
    fetch(
      `${BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${loginData?.admin_unit}&org_id=${loginData?.org_unit}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        setProsecutors(
          (data || []).filter(
            (d: any) =>
              d?.roles?.includes("Prosecutor") ||
              d?.roles?.includes("ProsecutionCaseProsecutor"),
          ),
        );
      })
      .catch(() => {
        showTooltip(t("error_fetching_prosecutors"), "error");
      })
      .finally(() => setLoadingList(false));
  }, [loginData, showPicker]);

  const handleAssign = () => {
    if (!selectedId) {
      showTooltip(t("prosecutor_is_required"), "error");
      return;
    }
    const selected = prosecutors.find(
      (p) => p.user?.id?.toString() === selectedId,
    );
    setSubmitting(true);
    editDefendantAppeal2({
      id: appealId,
      assigned_prosecutor:
        selected?.user?.first_name + " " + selected?.user?.last_name,
      assigned_prosecutor_id: selectedId,
      status: "ASSIGNED_TO_PROSECUTOR",
    })
      .then(() => {
        showTooltip(t("successfully_assigned"), "success");
        setReassigning(false);
        setSelectedId("");
        onAssigned();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setSubmitting(false));
  };

  const initials = (assignedProsecutor || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-base">👨‍💼</span>
        <h4 className="text-sm font-semibold text-gray-900">
          {t("assign_prosecutor")}
        </h4>
      </div>

      {isAssigned && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 shrink-0 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-sm font-semibold">
              {initials || "?"}
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">
                {t("assigned_prosecutor")}
              </p>
              <p className="text-sm font-semibold text-gray-900 truncate">
                {assignedProsecutor}
              </p>
            </div>
          </div>
          {!reassigning && (
            <button
              type="button"
              onClick={() => setReassigning(true)}
              className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              ↻ {t("reassign")}
            </button>
          )}
        </div>
      )}

      {showPicker && (
        <div className="space-y-3">
          <select
            className="w-full border border-gray-300 rounded-lg bg-white p-2 text-sm focus:outline-none focus:ring focus:ring-blue-300"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">
              {loadingList ? t("loading") : t("select_prosecutor")}
            </option>
            {prosecutors.map((p, index) => (
              <option key={index} value={p.user?.id || ""}>
                {p?.user?.first_name + " " + p?.user?.last_name}
              </option>
            ))}
          </select>

          <div className="flex justify-end gap-2">
            {reassigning && (
              <button
                type="button"
                onClick={() => {
                  setReassigning(false);
                  setSelectedId("");
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {t("cancel")}
              </button>
            )}
            <button
              type="button"
              onClick={handleAssign}
              disabled={submitting || !selectedId}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  {LoadingIcon}
                  {t("loading")}
                </>
              ) : isAssigned ? (
                t("reassign")
              ) : (
                t("assign")
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DefendantAssignProsecutorPanel;
